import { Link } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { formatPrice, resolvePhoto } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import { SellerBadge } from "@/components/SellerBadge";
import { MapPin, Truck, Package } from "lucide-react";

export const ListingCard = ({ listing, index = 0 }) => {
  const brand = useBrand();
  const { t } = useT();
  return (
    <Link to={`/volaille/${listing.id}`} data-testid={`listing-card-${listing.id}`}
      style={{ animationDelay: `${(index % 8) * 60}ms` }}
      className="group flex flex-col rounded-2xl overflow-hidden bg-card border border-border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-200 animate-fade-up">
      <div className="relative aspect-square overflow-hidden bg-muted">
        <img src={resolvePhoto(listing.image)} alt={listing.title} loading="lazy"
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <Badge className="absolute top-3 left-3 bg-white/90 text-secondary hover:bg-white border-0 pointer-events-none">
          {t(`categories.${listing.category}`)}
        </Badge>
        {listing.category !== "oeufs" && listing.stateLabel && (
          <Badge className="absolute top-3 right-3 bg-secondary/90 text-secondary-foreground border-0 pointer-events-none">
            {t(`states.${listing.state}`)}
          </Badge>
        )}
        {(listing.seller_premium || (listing.seller_badge && listing.seller_badge !== "none")) && (
          <span className="absolute bottom-3 left-3 pointer-events-none">
            <SellerBadge badge={listing.seller_badge} premium={listing.seller_premium} size="sm" />
          </span>
        )}
        {listing.status === "paused" && (
          <span className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-sm pointer-events-none">
            {t("listing.paused")}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" /> {listing.location}
        </div>
        <h3 className="mt-1.5 font-heading font-semibold text-base text-secondary line-clamp-1 group-hover:text-primary transition-colors">
          {listing.title}
        </h3>
        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Package className="h-3 w-3" /> {listing.quantity}</span>
          {listing.avg_weight && <span>{listing.avg_weight}</span>}
          {listing.delivery_available && (
            <span className="flex items-center gap-1 text-secondary"><Truck className="h-3 w-3" /> {t("listing.deliveryYes")}</span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">{listing.seller_name}</span>
          {listing.seller_reviews_count > 0 && <StarRating value={listing.seller_rating} count={listing.seller_reviews_count} size={11} />}
        </div>
        <div className="mt-auto pt-3 flex items-baseline gap-1">
          <span className="font-heading font-bold text-lg text-primary">
            {formatPrice(listing.price, brand.currencySymbol)}
          </span>
          <span className="text-xs text-muted-foreground">/ {listing.price_unit}</span>
        </div>
      </div>
    </Link>
  );
};
